/**
 * `<tableau-area>`: one half of a team's tableau, laid on the felt in front of
 * one of its seats - melds still building toward a Book in front of the
 * team's first seat, finished Books in front of the second.
 *
 * One click, one meaning. A pile the selection can join takes the cards; one
 * it cannot, while cards are held out, shakes and says why; anything else is
 * being looked at rather than played into, so it asks for the read-only tray.
 */

import {
    type Card,
    type Meld,
    type MeldableRank,
    type Team,
    PURE_BOOK_BONUS,
    WILD_BOOK_BONUS,
    isPure
} from "@ryzun/haf-saloon-game-engine";
import type { TeamColor } from "../../server/protocol.js";
import { getRankWord } from "../cards.js";
import { h, keepFocus, setAttribute } from "../dom.js";
import { type TableauKind, emit } from "../events.js";
import {
    describeMeld,
    findAddedCards,
    getBookCardIds,
    selectMelds
} from "../meld-text.js";
import type { LayDownSurface, StagedTile } from "../play.js";
import { type BookTally, formatPoints } from "../tally.js";
import { BaseElement } from "./base-element.js";
import styles from "./tableau-area.css";
import "./playing-card.js";

export interface TableauModel {
    readonly team: Team;
    readonly kind: TableauKind;
    /** "Your team", or the names of the players who sit at it. */
    readonly name: string;
    readonly color: TeamColor;
    /** Whether this is the viewer's own team; only their piles take cards. */
    readonly isOwn: boolean;
    /** Ranks the current selection could join; empty outside the play phase. */
    readonly addableRanks: readonly MeldableRank[];
    /** Whether the player is holding cards out, which is what makes a click a play. */
    readonly hasSelection: boolean;
    /** Present only on the viewer's own meld row, which is where new melds land. */
    readonly layDown: LayDownSurface | null;
    /** Each book as the end-of-round count has left it, or null while there is no count on. */
    readonly tally: ReadonlyMap<MeldableRank, BookTally> | null;
}

/** A column of cards, each sliding down from the one above it. */
function createStack(cards: readonly Card[]): HTMLSpanElement {
    const stack = h("span", { class: "stack" });

    stack.style.setProperty("--cards", String(cards.length));
    stack.append(...cards.map((card, index) => {
        const face = document.createElement("playing-card");

        face.card = card;
        face.style.setProperty("--index", String(index));

        return face;
    }));

    return stack;
}

/**
 * Two backs squared up under a solid cap that names the rank. The cap says
 * pure or wild, so cards a book takes go in at the bottom: while it receives,
 * the stack lifts as one lid and the card it took slides in underneath, face
 * up so the table sees what went in.
 */
function createBook(meld: Meld, received: Card | null): HTMLSpanElement {
    const cap = document.createElement("playing-card");

    cap.cap = { rank: meld.rank, pure: isPure(meld) };

    const lid = h(
        "span",
        { class: "lid" },
        document.createElement("playing-card"),
        document.createElement("playing-card"),
        cap
    );

    if (received === null) {
        return h("span", { class: "book" }, lid);
    }

    const tuck = document.createElement("playing-card");

    tuck.card = received;
    tuck.classList.add("tuck");

    return h("span", { class: "book" }, tuck, lid);
}

/** How long a book takes to lift, take a card underneath and settle. */
const RECEIVE_MS = 700;

/** A book partway through taking cards. */
interface Receipt {
    /** When the lift started, on the `performance.now()` clock. */
    readonly start: number;
    /** The card shown sliding in: the last one the book took. */
    readonly card: Card;
}

export class TableauArea extends BaseElement {
    #model: TableauModel | null = null;

    /** Each book's cards as last drawn, to tell when one takes more; null before the first. */
    #bookCardIds: ReadonlyMap<MeldableRank, ReadonlySet<string>> | null = null;
    #bookTeamId: string | null = null;

    /**
     * The books still receiving. The row is rebuilt on every render, so a
     * render mid-lift picks the animation up where it was rather than starting
     * it over or cutting it off.
     */
    #receiving: ReadonlyMap<MeldableRank, Receipt> = new Map();

    readonly #row = h("ul", { class: "row" });

    constructor() {
        super(styles);

        this.root.append(h("div", { class: "surface" }, this.#row));
    }

    set model(value: TableauModel | null) {
        this.#model = value;
        this.invalidate();
    }

    /** Where a book or meld lies on the felt, or null when this area shows none of that rank. */
    pileBox(rank: MeldableRank): DOMRect | null {
        const item = this.#row.querySelector(`li[data-rank="${CSS.escape(rank)}"]`);

        return item?.getBoundingClientRect() ?? null;
    }

    protected render(): void {
        const model = this.#model;

        if (model === null) {
            return;
        }

        setAttribute(this, "kind", model.kind);
        setAttribute(this.#row, "aria-label", `${model.name}, ${model.kind}`);

        const melds = selectMelds(model.team.melds, model.kind);
        const now = performance.now();

        if (model.kind === "books") {
            this.#trackBooks(model.team.id, melds, now);
        }

        keepFocus(this.root, () => {
            this.#row.replaceChildren(
                ...this.#renderLayDownControls(model.layDown),
                ...melds.map((meld) => this.#renderPile(model, meld, now)),
                ...this.#renderLayDown(model.layDown)
            );
        });
    }

    /** Starts a lift for every book that grew, and lets finished ones go. */
    #trackBooks(teamId: string, books: readonly Meld[], now: number): void {
        const previous = this.#bookTeamId === teamId ? this.#bookCardIds : null;
        const added: ReadonlyMap<MeldableRank, readonly Card[]> = previous === null
            ? new Map()
            : findAddedCards(previous, books);
        const started = [...added].flatMap(([rank, cards]): [MeldableRank, Receipt][] => {
            const card = cards.at(-1);

            return card === undefined ? [] : [[rank, { start: now, card }]];
        });

        this.#receiving = new Map([
            ...[...this.#receiving].filter(([, receipt]) => now - receipt.start < RECEIVE_MS),
            ...started
        ]);
        this.#bookCardIds = getBookCardIds(books);
        this.#bookTeamId = teamId;
    }

    #renderPile(model: TableauModel, meld: Meld, now: number): HTMLLIElement {
        const addable = model.isOwn && model.addableRanks.includes(meld.rank);
        const label = addable
            ? `Add the selected cards to your team's ${describeMeld(meld)}`
            : model.isOwn ? describeMeld(meld) : `${model.name}, ${describeMeld(meld)}`;

        const receipt = model.kind === "books" ? this.#receiving.get(meld.rank) : undefined;
        const tally = model.kind === "books" ? model.tally?.get(meld.rank) : undefined;
        const body = model.kind === "books"
            ? [createBook(meld, receipt?.card ?? null), this.#renderBookLabel(meld, tally?.left ?? meld.cards.length)]
            : [createStack(meld.cards)];

        const button = h(
            "button",
            {
                "type": "button",
                "class": addable ? "pile addable" : "pile",
                "aria-label": label,
                "data-focus-key": `meld-${meld.rank}`
            },
            ...body
        );

        const item = h("li", { "data-rank": meld.rank }, button);

        if (receipt !== undefined) {
            item.classList.add("receiving");
            item.style.setProperty("--receive-ms", `${RECEIVE_MS}ms`);
            item.style.setProperty("--receive-delay", `${Math.round(receipt.start - now)}ms`);
        }

        // Rebuilt with every card the count moves, so the mark picks up where it was.
        if (tally !== undefined && tally.scoredAt !== null) {
            const bonus = isPure(meld) ? PURE_BOOK_BONUS : WILD_BOOK_BONUS;

            item.classList.add("scored");
            item.style.setProperty("--scored-delay", `${Math.round(tally.scoredAt - now)}ms`);
            item.append(h("span", { "class": "bonus", "aria-hidden": "true" }, formatPoints(bonus)));
        }

        button.addEventListener("click", () => this.#pick(model, meld, item));

        return item;
    }

    #renderBookLabel(meld: Meld, count: number): HTMLSpanElement {
        return h(
            "span",
            { "class": "book-label", "aria-hidden": "true" },
            isPure(meld) ? "PURE · " : "WILD · ",
            h("b", {}, String(count))
        );
    }

    #pick(model: TableauModel, meld: Meld, item: HTMLLIElement): void {
        if (model.isOwn && model.addableRanks.includes(meld.rank)) {
            emit(this, "meld-add", { rank: meld.rank });
            return;
        }

        if (model.isOwn && model.hasSelection) {
            item.classList.remove("refused");
            // Reading layout restarts the animation when the same pile is refused twice.
            void item.offsetWidth;
            item.classList.add("refused");
            emit(this, "meld-refuse", { rank: meld.rank });
            return;
        }

        emit(this, "meld-inspect", { teamId: model.team.id, kind: model.kind });
    }

    #renderLayDown(layDown: LayDownSurface | null): readonly HTMLLIElement[] {
        if (layDown === null) {
            return [];
        }

        const items = layDown.staged.map((tile) => this.#renderStaged(tile));

        if (layDown.canCreate) {
            const ghost = h(
                "button",
                {
                    "type": "button",
                    "class": "pile ghost",
                    "data-focus-key": "new-meld",
                    "aria-label": layDown.opensRound
                        ? "Set these cards down as a new meld toward your opener"
                        : "Play these cards as a new meld"
                },
                h(
                    "span",
                    { "class": "new-tile", "aria-hidden": "true" },
                    h("span", { class: "plus" }, "+"),
                    h("span", { class: "new-label" }, "New meld")
                )
            );

            ghost.addEventListener("click", () => emit(this, "meld-create", null));
            items.push(h("li", { class: "fixed" }, ghost));
        }

        return items;
    }

    /**
     * Only while an opener is being assembled: once the team is in the game a
     * new meld goes straight down, so there is nothing to settle. It leads the
     * row because the hand fans up over the row's far end, where it was hard
     * to reach.
     */
    #renderLayDownControls(layDown: LayDownSurface | null): readonly HTMLLIElement[] {
        if (layDown === null || layDown.staged.length === 0) {
            return [];
        }

        const lay = h(
            "button",
            {
                "type": "button",
                "class": layDown.canLayDown ? "lay primary" : "lay",
                "data-focus-key": "lay-down",
                "disabled": !layDown.canLayDown
            },
            "Lay it down"
        );
        const clear = h(
            "button",
            {
                "type": "button",
                "class": "lay clear",
                "data-focus-key": "lay-down-clear",
                "aria-label": "Take every staged meld back into your hand"
            },
            "Clear melds"
        );
        const total = layDown.opensRound
            ? `${layDown.stagedValue} pts of ${layDown.roundMinimum}`
            : `${layDown.stagedValue} pts`;

        lay.addEventListener("click", () => emit(this, "lay-down", null));
        clear.addEventListener("click", () => emit(this, "lay-down-clear", null));

        return [h("li", { class: "fixed laydown" }, h("span", { class: "total" }, total), lay, clear)];
    }

    #renderStaged(tile: StagedTile): HTMLLIElement {
        const button = h(
            "button",
            {
                "type": "button",
                "class": "pile staged",
                "data-focus-key": `staged-${tile.rank}`,
                "aria-label": `Take the ${getRankWord(tile.rank, true)} back into your hand, ${tile.value} points`
            },
            createStack(tile.cards),
            h("span", { "class": "take-back", "aria-hidden": "true" }, "×")
        );

        button.addEventListener("click", () => emit(this, "meld-unstage", { rank: tile.rank }));

        return h("li", {}, button);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "tableau-area": TableauArea;
    }
}

customElements.define("tableau-area", TableauArea);
